import { useState } from 'react';
import { FaCopy, FaCheck, FaTimes, FaShieldAlt, FaPen } from 'react-icons/fa';
import AvatarImage from '@/components/common/AvatarImage';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/components/common/ToastProvider';
import { useCurrentUser } from '@/hooks/useCurrentUser';

export interface UserProfileData {
  id: string;
  username: string;
  status: 'online' | 'away' | 'offline' | 'idle' | 'dnd' | string;
  lastSeenAt?: string | number | null;
  avatarUrl?: string | null;
  email?: string | null;
  bio?: string | null;
  role?: string | null;
  isAdmin?: boolean;
}

interface UserProfileModalProps {
  user: UserProfileData | null;
  isOpen: boolean;
  onClose: () => void;
  onEditBio?: () => void;
}

export default function UserProfileModal({
  user,
  isOpen,
  onClose,
  onEditBio,
}: UserProfileModalProps) {
  const [copied, setCopied] = useState(false);
  const currentUser = useCurrentUser();
  const toast = useToast();

  if (!isOpen || !user) return null;

  const isSelf = String(currentUser?.id) === String(user.id) || currentUser?.username === user.username;

  const handleCopyId = () => {
    navigator.clipboard.writeText(user.id);
    setCopied(true);
    toast.success('User ID copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-500';
      case 'away':
      case 'idle':
        return 'bg-yellow-500';
      case 'dnd':
        return 'bg-brand-error';
      case 'offline':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'online':
        return 'Online';
      case 'away':
      case 'idle':
        return 'Idle';
      case 'dnd':
        return 'Do Not Disturb';
      case 'offline':
        return 'Offline';
      default:
        return 'Offline';
    }
  };

  const formattedLastSeen = user.lastSeenAt
    ? typeof user.lastSeenAt === 'number'
      ? formatDistanceToNow(new Date(user.lastSeenAt > 1e11 ? user.lastSeenAt : user.lastSeenAt * 1000), { addSuffix: true })
      : formatDistanceToNow(new Date(user.lastSeenAt), { addSuffix: true })
    : null;

  const handleEditProfile = () => {
    onClose();
    if (onEditBio) {
      onEditBio();
    } else {
      window.dispatchEvent(new CustomEvent('open-user-settings', { detail: { tab: 'profile' } }));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-m3-fade-scale"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-3xl bg-brand-surface/95 dark:bg-[#1f1c48]/95 backdrop-blur-xl border border-brand-outline-variant/60 shadow-2xl overflow-hidden animate-m3-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Banner with gradient accent */}
        <div className="h-24 bg-gradient-to-r from-primary-600 via-amber-500 to-purple-600 relative">
          <button
            onClick={onClose}
            aria-label="Close profile"
            className="absolute top-3 right-3 p-2 rounded-full bg-black/30 text-white hover:bg-black/50 transition-all active:scale-95 cursor-pointer"
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* Profile Card Body */}
        <div className="px-6 pb-6 pt-0 relative">
          {/* Large Avatar with Status Ring */}
          <div className="relative -mt-12 mb-3 inline-block">
            <div className="w-20 h-20 rounded-full bg-primary-700 border-4 border-brand-surface dark:border-[#1f1c48] flex items-center justify-center overflow-hidden shadow-xl">
              <AvatarImage
                src={user.avatarUrl || undefined}
                alt={user.username}
                fallback={
                  <span className="text-white font-bold text-2xl">
                    {user.username?.[0]?.toUpperCase() || 'U'}
                  </span>
                }
              />
            </div>
            <div
              className={`absolute bottom-1 right-1 w-5 h-5 ${getStatusColor(
                user.status,
              )} rounded-full border-3 border-brand-surface dark:border-[#1f1c48] shadow-md`}
              title={getStatusLabel(user.status)}
            />
          </div>

          {/* User Details */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-brand-on-background dark:text-white truncate">
                {user.username}
              </h2>
              {user.isAdmin && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  <FaShieldAlt size={9} /> ADMIN
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 mt-1 text-xs text-brand-outline dark:text-[#c4b5fd]">
              <span className="font-semibold text-brand-on-surface-variant dark:text-brand-on-surface">
                {getStatusLabel(user.status)}
              </span>
              {formattedLastSeen && user.status === 'offline' && (
                <>
                  <span>•</span>
                  <span>Last active {formattedLastSeen}</span>
                </>
              )}
            </div>
          </div>

          {/* About Me / Bio Card */}
          <div className="bg-brand-surface-variant/30 dark:bg-black/20 rounded-2xl p-3.5 border border-brand-outline-variant/40 mb-3.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-brand-outline dark:text-[#c4b5fd] mb-1.5 flex items-center justify-between">
              <span>About Me</span>
              {isSelf && (
                <button
                  type="button"
                  onClick={handleEditProfile}
                  className="text-[11px] text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-1 cursor-pointer font-normal normal-case"
                >
                  <FaPen size={9} /> Edit
                </button>
              )}
            </div>
            {user.bio ? (
              <p className="text-xs sm:text-sm text-brand-on-surface dark:text-[#e0e7ff] leading-relaxed whitespace-pre-wrap break-words">
                {user.bio}
              </p>
            ) : (
              <p className="text-xs text-brand-outline dark:text-brand-on-surface-variant/70 italic">
                {isSelf ? 'You have not added a bio yet. Click Edit to add one!' : 'No bio provided.'}
              </p>
            )}
          </div>

          {/* Quick Info Box */}
          <div className="bg-brand-surface-variant/30 dark:bg-black/20 rounded-2xl p-3.5 border border-brand-outline-variant/40 mb-5 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-brand-outline dark:text-[#c4b5fd] font-medium">User ID</span>
              <button
                onClick={handleCopyId}
                className="flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 transition-colors font-mono cursor-pointer"
                title="Copy User ID"
              >
                <span>{user.id.slice(0, 8)}...</span>
                {copied ? <FaCheck size={11} className="text-green-400" /> : <FaCopy size={11} />}
              </button>
            </div>
            {user.role && (
              <div className="flex items-center justify-between text-xs pt-1 border-t border-brand-outline-variant/30">
                <span className="text-brand-outline dark:text-[#c4b5fd] font-medium">Server Role</span>
                <span className="font-semibold text-brand-on-surface dark:text-[#e0e7ff]">{user.role}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {isSelf && (
              <button
                onClick={handleEditProfile}
                className="btn btn-primary flex-1 py-2.5 rounded-full font-semibold text-xs sm:text-sm shadow-md flex items-center justify-center gap-2 cursor-pointer"
              >
                <FaPen size={11} />
                Edit Profile
              </button>
            )}
            <button
              onClick={onClose}
              className={`${
                isSelf ? 'btn btn-outlined' : 'btn btn-primary w-full'
              } py-2.5 px-4 rounded-full font-medium text-xs sm:text-sm border border-brand-outline-variant/80 text-brand-on-surface hover:bg-white/10`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
